import { ChangeEvent, Fragment, ReactNode, useState } from "react"
import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import Autocomplete, {
  AutocompleteClassKey,
} from "@material-ui/lab/Autocomplete"
import { useField } from "formik"

import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { useI18n } from "providers/I18n"
import { ClassNameMap } from "@material-ui/core/styles/withStyles"
import { OutlinedInputClassKey } from "@material-ui/core"

export interface Option {
  label: string
  value: string
  symbol?: string
}

export type ComboboxProps<T extends Option = Option> = Omit<
  TextFieldProps,
  "onChange" | "value"
> & {
  name: string
  errorMessage?: string
  initialInputValue?: string
  options: T[]
  //
  onChange: (event: ChangeEvent<any>, value: T | null) => void
  value?: string
  //
  getOptionDisabled?: ((option: T) => boolean) | undefined
  groupBy?: ((option: T) => string) | undefined
  renderOption?: (option: T) => ReactNode
  classes?: Partial<Record<AutocompleteClassKey, string>>
  textClasses?: Partial<ClassNameMap<OutlinedInputClassKey>>
  disableClearable?: boolean
  hasPopupIcon?: boolean
  getSelectedOptionLabel?: (option?: T) => string
}

export function Combobox<T extends Option = Option>(props: ComboboxProps<T>) {
  const {
    name,
    label,
    errorMessage,
    error,
    fullWidth = true,
    helperText,
    initialInputValue,
    //
    options,
    value = "",
    onChange,
    getOptionDisabled,
    groupBy,
    renderOption,
    classes,
    textClasses,
    size = "small",
    disabled = false,
    disableClearable = false,
    hasPopupIcon = true,
    getSelectedOptionLabel = (option?: T) => option?.label,
    ...rest
  } = props

  const { t } = useI18n()
  const hasError = !!errorMessage || error
  const helper = hasError ? t(errorMessage!) : helperText

  const [inputValue, setInputValue] = useState<string>(
    () => initialInputValue || "",
  )

  let option: T | null = null
  if (value) {
    const selectedOption = options.find((option) => option.value === value)
    const label = getSelectedOptionLabel(selectedOption) ?? inputValue
    option = { value, label } as T
  }

  const handleInputChange = (_: ChangeEvent<{}>, value: string) => {
    setInputValue(value)
  }

  return (
    <Autocomplete<T>
      size={size}
      getOptionLabel={(option) => option.label}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disabled={disabled}
      //@ts-ignore
      disableClearable={disableClearable}
      //
      value={option}
      onChange={onChange}
      options={options}
      getOptionSelected={(option, value) => option.value === value.value}
      getOptionDisabled={getOptionDisabled}
      groupBy={groupBy}
      forcePopupIcon={hasPopupIcon}
      //
      fullWidth={fullWidth}
      popupIcon={<DropdownIcon />}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          variant="outlined"
          margin="none"
          {...rest}
          {...params}
          name={name}
          label={label}
          error={hasError}
          helperText={helper}
          InputProps={{
            ...params.InputProps,
            classes: textClasses,
            endAdornment: <Fragment>{params.InputProps.endAdornment}</Fragment>,
          }}
        />
      )}
      classes={classes}
    />
  )
}

type PropsHandledByFormik = "onChange" | "value" | "error"
type NameRequired = { name: string }

export type ComboboxFormikProps<T extends Option> = Omit<
  ComboboxProps<T>,
  PropsHandledByFormik
> &
  NameRequired

export function ComboboxFormik<T extends Option>(
  props: ComboboxFormikProps<T>,
) {
  const { name, ...rest } = props
  const [{ value, onChange, ...field }, { error, touched }, { setValue }] =
    useField<string>(name)
  const hasError = !!(touched && error)
  const errorMessage = hasError ? error : undefined

  const handleChange = (_: any, option: Option | null) => {
    if (option) {
      setValue(option.value.toString())
    } else {
      setValue("")
    }
  }

  return (
    <Combobox
      {...rest}
      value={value}
      onChange={handleChange}
      errorMessage={errorMessage}
      {...field}
    />
  )
}
